<script setup lang="ts">
import { isNull } from 'lodash'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { onMounted, onUnmounted } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import type { ApolloQueryResult } from '@apollo/client'
import AppHeader from '@/components/layouts/header/AppHeader.vue'
import AppFooter from '@/components/layouts/AppFooter.vue'
import SignInView from '@/components/views/SignInView.vue'
import { useUserStore } from '@/stores/user.store.ts'
import { HEADER_PADDING_TOP_PX } from '@/config/visual.const.ts'
import { DEFAULT_ENVIRONMENT, getDefaultClientEnv } from '@/config/env.const.ts'
import router, { buildPathByErrorName } from '@/router.ts'
import { BackendErrorCode, Environment, ErrorName, WebsocketConnectionState } from '@/enums.ts'
import { FindProjectsCompactQuery } from '@/components/projects/graphql/project.queries.ts'
import { GetCurrentUserInfoAndProjects } from '@/components/users/graphql/user.queries.ts'
import { WSLinkStore } from '@/graphql.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import type { Project } from '@/components/projects/interfaces.ts'
import type { AuthorizeUserResponse } from '@/components/users/interfaces.ts'
import { useTranslationStore } from '@/stores/translation.store.ts'

const route = useRoute()
const uiStore = useUiStore()
const userStore = useUserStore()
const { isAuthorized } = storeToRefs(userStore)
const { isLoaded: isTranslationsLoaded } = storeToRefs(useTranslationStore())
const {
  onResult: onCurrentUserInfoAndProjectsLoaded,
  onError: onCurrentUserInfoAndProjectsLoadingFailed,
  load: loadCurrentUserInfoAndProjects
} = useLazyQuery(GetCurrentUserInfoAndProjects)
const {
  onResult: onUserProjectsLoaded,
  onError: onUserProjectsLoadingFailed,
  load: loadUserProjects
} = useLazyQuery<{ projects: Project }>(FindProjectsCompactQuery)

onCurrentUserInfoAndProjectsLoaded(({ data: { user, projects } }: ApolloQueryResult<{
  user: AuthorizeUserResponse
  projects: Project[]
}>) => {
  userStore.authorize(user)
  userStore.setProjectKeyFromRouteParams(route.params)
  userStore.setProjectsAndSelectIfProjectKeyProvidedInUrl(projects, onProjectSelectionFailed)
})

onCurrentUserInfoAndProjectsLoadingFailed((err) => {
  if (err.graphQLErrors.map((err) => err.message).includes(BackendErrorCode.Unauthorized)) {
    userStore.unAuthorize()
  }
})

// @ts-expect-error Jevgeni fix it later
onUserProjectsLoaded(({ data: { projects } }: ApolloQueryResult<{
  projects: Project[]
}>) => {
  userStore.setProjectsAndSelectIfProjectKeyProvidedInUrl(projects, onProjectSelectionFailed)
})

onUserProjectsLoadingFailed((err) => {
  console.error('onUserProjectsLoadingFailed | Error:', err)
})

function onProjectSelectionFailed (): void {
  router.push({
    path: buildPathByErrorName(ErrorName.ProjectNotFound)
  })
}

function onScroll (): void {
  uiStore.setStickyHeader(window.scrollY > HEADER_PADDING_TOP_PX)
}

function onLoginSucceed (user: AuthorizeUserResponse) {
  userStore.authorize(user)
  loadUserProjects()
}

onMounted(async () => {
  await router.isReady()
  window.addEventListener('scroll', onScroll)

  try {
    await loadCurrentUserInfoAndProjects()
  } catch (err) {
  }

  WSLinkStore[getDefaultClientEnv()].client.on('closed' , (event) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Closed | Event:', event)
    userStore.setWebsocketConnectionState(WebsocketConnectionState.Disconnected)
  })
  WSLinkStore[getDefaultClientEnv()].client.on('connected' , (event) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Connected | Event:', event)
    userStore.setWebsocketConnectionState(WebsocketConnectionState.Connected)
  })
  WSLinkStore[getDefaultClientEnv()].client.on('connecting' , (event) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Connecting | Event:', event)
    userStore.setWebsocketConnectionState(WebsocketConnectionState.Connecting)
  })
  WSLinkStore[getDefaultClientEnv()].client.on('error' , (err) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Error:', err)
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <div
    v-if="DEFAULT_ENVIRONMENT !== Environment.Production"
    class="theenv fixed bg-indigo-500 text-yellow-300 text-center z-50 font-semibold h-9 font text-xl"
  >
    {{ DEFAULT_ENVIRONMENT.toUpperCase() }}
  </div>
  <div v-if="isNull(isAuthorized)"></div>
  <div v-else-if="isAuthorized && isTranslationsLoaded" class="min-h-screen flex flex-col bg-[linear-gradient(98.89deg,#D9E7FB_0%,#FFEBB9_96.04%)]">
    <AppHeader/>
    <div class="flex-grow">
      <RouterView />
    </div>
    <AppFooter/>
  </div>
  <div v-else>
    <SignInView @onLoginSucceed="onLoginSucceed" v-if="isTranslationsLoaded"/>
  </div>
</template>
<style scoped>
.theenv {
  left: -53px;
  top: 29px;
  width: 200px;
  transform: rotate(315deg);
  line-height: 35px;
}
</style>
